import { useState } from "react";
import { signIn, signUp, setAuthToken } from "../api.js";

export default function AuthPage({ onSignIn, onBack }) {
  const [mode,     setMode]     = useState("signin");  // signin | signup
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const validate = () => {
    if (mode === "signup" && !name.trim()) return "Name is required.";
    if (!email.includes("@"))              return "Enter a valid email.";
    if (password.length < 6)              return "Password must be at least 6 characters.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setLoading(true);

    try {
      const data = mode === "signup"
        ? await signUp(email, password, name)
        : await signIn(email, password);

      // Store the JWT so every subsequent API call is authenticated
      setAuthToken(data.token);

      onSignIn({
        id:     data.user_id,
        name:   data.name,
        email:  data.email,
        avatar: (data.name || data.email)[0].toUpperCase(),
      });
    } catch (ex) {
      setError(ex.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(m => m === "signin" ? "signup" : "signin");
    setError("");
  };

  return (
    <div style={{
      minHeight: "100vh",
      maxHeight: "100vh",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: 24,
      background: "var(--bg)",
    }} className="landing-hero-bg">

      {/* Back */}
      {onBack && (
        <button onClick={onBack} className="btn btn-ghost"
          style={{ position: "absolute", top: 20, left: 20, fontSize: 13 }}>
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>arrow_back</span>
          Back
        </button>
      )}

      {/* Card */}
      <div className="card animate-scaleIn" style={{
        width: "100%", maxWidth: 420,
        padding: "clamp(28px, 6vw, 40px) clamp(22px, 6vw, 36px)",
        margin: "auto",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32, gap: 10 }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            background: "linear-gradient(135deg, var(--accent), var(--sky))",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 24px var(--accent-glow)",
          }}>
            <span
              className="material-symbols-rounded"
              style={{ color: "white", fontSize: 28, fontVariationSettings: "'FILL' 1" }}
            >
              auto_stories
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--accent-2)",
              marginBottom: 6,
            }}>
              StudyMind AI
            </div>
            <h1 style={{
              fontFamily: "var(--font-display)", fontSize: "1.5rem",
              fontWeight: 800, letterSpacing: 0, marginBottom: 4,
            }}>
              {mode === "signin" ? "Welcome back" : "Create your account"}
            </h1>
            <p style={{ color: "var(--muted)", fontSize: 13 }}>
              {mode === "signin"
                ? "Sign in to continue studying with your notebooks."
                : "Create an account to save notebooks, notes, and progress."}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {mode === "signup" && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>
                Full name
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter your full name"
                style={{ width: "100%" }}
              />
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ width: "100%" }}
              required
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", display: "block", marginBottom: 6 }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              style={{ width: "100%" }}
              required
            />
          </div>

          {error && (
            <div style={{
              background: "var(--rose-soft)",
              border: "1px solid rgba(244,63,94,0.25)",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              color: "var(--rose)",
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ marginTop: 4, width: "100%", justifyContent: "center" }}
          >
            {loading
              ? (mode === "signin" ? "Signing in…" : "Creating account…")
              : (mode === "signin" ? "Sign in" : "Create account")}
          </button>
        </form>

        {/* Switch mode */}
        <p style={{ textAlign: "center", fontSize: 13, color: "var(--muted)", marginTop: 20 }}>
          {mode === "signin" ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={switchMode}
            style={{
              background: "none", border: "none", padding: 0,
              color: "var(--accent)", fontWeight: 600, cursor: "pointer", fontSize: 13,
            }}
          >
            {mode === "signin" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
